import { FC, useEffect } from 'react'
import { useRiskQuestionnaireStore } from '../../state-management/questionnaireStore'
import RiskProfileRiveComponent from './RiskProfileRiveComponent'
import {
  disableHardwareBack,
  handleClose,
  openRebalancePlanSummary,
  openRebalancePlanSummaryRemove,
  trackEvent
} from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import { RiveFit } from '../ui-components/GenericRiveComponent'
import { useQueryClient } from '@tanstack/react-query'
import { getBottomPadding } from '../../utils/sessionStorage'

interface RiskProfileResultProps {
  from: string | null
  score: number
  profile: string
  navigate: (path: string) => void
}

const RiskProfileResult: FC<RiskProfileResultProps> = ({ from, score, profile, navigate }) => {
  useEffect(() => {
    disableHardwareBack()
  }, [])

  const queryClient = useQueryClient()
  const resetQuestionnaire = useRiskQuestionnaireStore((state) => state.resetQuestionnaire)

  // Defensive check - ensure we have valid values (after all hooks are called)
  if (!profile || typeof profile !== 'string') {
    console.error('Invalid profile received:', profile)
    return (
      <div className='bg-white p-8 md:p-12 rounded-xl shadow-lg w-full max-w-lg mx-auto text-center'>
        <h2 className='text-3xl font-bold text-red-600 mb-4'>Error</h2>
        <p className='text-lg text-gray-600 mb-6'>Invalid risk profile data received</p>
        <button
          onClick={() => window.location.reload()}
          className='w-full bg-blue-600 text-white font-semibold py-3 px-6 rounded-xl hover:bg-blue-700 transition-colors'
        >
          Reload Page
        </button>
      </div>
    )
  }

  return (
    <div
      style={{ paddingBottom: `calc(${getBottomPadding()}px + 20px)` }}
      className='bg-white min-h-screen flex flex-col items-start justify-end w-full relative'
    >
      <div className='flex-grow mb-8 w-full relative'>
        <RiskProfileRiveComponent profile={profile} riveFit={RiveFit.HEIGHT} isLoop={true} className='' />
      </div>

      {/* Risk Profile Text */}
      <p className='text-sm text-gray-500 mb-2 ml-5'>Your risk profile is</p>
      <h1 className='text-[32px] font-medium text-black mb-4 ml-5'>{profile.toUpperCase()}</h1>

      {/* Description */}
      <p className='text-base text-gray-700 text-left max-w-sm mb-12 ml-5'>
        {profile === 'Conservative' && 'You prefer low risk, steady growth, and protecting your money as it grows.'}
        {profile === 'Balanced' &&
          'You want a balance of growth and stability. You prefer a balance of risk and returns, but with enough cushion for peace of mind.'}
        {profile === 'Aggressive' && "You're okay with taking high risks for the chance of higher returns."}
        {profile === 'Very Conservative' &&
          'Your priority is safety and capital protection. You want minimal risk, even if it means lower returns.'}
        {profile === 'Very Aggressive' &&
          "You're comfortable with very high risk, aiming for the highest possible long-term returns."}
      </p>

      {/* Action Buttons */}
      <div className='w-full space-y-3 justify-self-center px-5'>
        <button
          onClick={() => {
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                buttonLabel: 'View rebalancing plan',
                eventId: 'VIEW_REBALANCE_PLAN_BUTTON'
              }
            })
            queryClient.invalidateQueries({ queryKey: ['smartRebalance'] }).then(() => {
              navigate('/rebalance-plan?from=first-time-risk-assessment')
            })
          }}
          className='w-full bg-black text-white font-normal py-4 px-6 rounded-xl hover:bg-gray-800 transition-colors focus:outline-none'
        >
          View rebalancing plan
        </button>
        <button
          onClick={() => {
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                buttonLabel: 'Retake test',
                eventId: 'RETAKE_TEST_BUTTON'
              }
            })
            resetQuestionnaire()
            if (from === 'rebalance-plan') navigate('/rebalance-questionnaire?from=rebalance-plan')
            else navigate('/rebalance-questionnaire')
          }}
          className='w-full bg-white text-black font-normal py-4 px-6 rounded-xl border-2 focus:outline-none border-gray-300 hover:bg-gray-50 transition-colors'
        >
          Retake test
        </button>
      </div>
    </div>
  )
}

export default RiskProfileResult
